// File: src/components/pages/students/StudentList.jsx
// UPDATED: Added New Student button at the top-right

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Search, Edit, Trash2, Eye, Mail, Phone, AlertCircle, Copy, Plus } from 'lucide-react';
import { studentAPI } from '../../../services/api';
import StudentDetailsModal from '../../modals/StudentDetailsModal';
import EditStudentModal from '../../modals/EditStudentModal';

const StudentList = ({ status, onNewStudent }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal state for View
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Modal state for Edit
  const [selectedStudentForEdit, setSelectedStudentForEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadStudents(status);
  }, [status]);

  const loadStudents = async (filterStatus) => {
    setIsLoading(true);
    setError(null);
     
    try {
      const response = await studentAPI.getStudentsByStatus(
        filterStatus,
        getAccessTokenSilently
      );

      console.log(`Loading students with status: ${filterStatus}`);
      if (response.success) {
        setStudents(response.data || []);
      } else {
        setError(response.message || 'Failed to load students');
        setStudents([]);
      }
    } catch (err) {
      console.error('Failed to load students:', err);
      setError('Failed to load students. Please try again.');
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.phone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleView = (studentId) => {
    console.log('View student:', studentId);
    setSelectedStudentId(studentId);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedStudentId(null);
  };

  const handleEdit = (student) => {
    console.log('Edit student:', student.id);
    setSelectedStudentForEdit(student);
    setShowEditModal(true);
  };

  const handleCopyUrl = (student) => {
    const token = student.invitationToken;

    if (!token) {
      alert('No invitation token found for this student.');
      console.error('Student object:', student);
      return;
    }

    const signupUrl = process.env.REACT_APP_STUDENT_SIGNUP_URL;
    const finalUrl = `${signupUrl}?token=${token}`;

    navigator.clipboard.writeText(finalUrl).then(() => {
      alert('Registration URL copied to clipboard!');
    }).catch(() => {
      // Fallback for browsers that block clipboard API
      prompt('Copy this registration URL:', finalUrl);
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedStudentForEdit(null);
  };

  const handleEditSuccess = async () => {
    console.log('Student updated successfully, refreshing list...');
    // Reload students after successful edit
    await loadStudents(status);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student? This will set their status to "deleted".')) {
      return;
    }

    try {
      console.log('Delete student:', studentId);
      
      const response = await studentAPI.deleteStudent(
        studentId,
        false, // soft delete
        getAccessTokenSilently
      );

      if (response.success) {
        // Reload students after deletion
        await loadStudents(status);
        alert('Student deleted successfully');
      } else {
        alert('Failed to delete student: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting student:', err);
      alert('Failed to delete student: ' + err.message);
    }
  };

  const getStatusBadge = (studentStatus) => {
    const statusConfig = {
      active: { label: 'Active', color: '#10b981' },
      approve: { label: 'Pending Approval', color: '#f59e0b' },
      invited: { label: 'Invited', color: '#3b82f6' },
      inactive: { label: 'Inactive', color: '#6b7280' },
      passed: { label: 'Passed', color: '#2563eb' },
      delete: { label: 'Delete', color: '#ef4444' }
    };

    const config = statusConfig[studentStatus] || statusConfig.active;

    return (
      <span 
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    // Format: +447423393876 → +44 7423 393876
    if (phone.startsWith('+44')) {
      return phone.replace(/(\+44)(\d{4})(\d{6})/, '$1 $2 $3');
    }
    return phone;
  };

  return (
    <div className="user-list-container">
      {/* ADDED: Search Bar with New Student Button */}
      <div className="search-bar-with-action">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search students by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button onClick={onNewStudent} className="btn-new">
          <Plus size={20} />
          New Student
        </button>
      </div>

      {/* Users Count */}
      <div className="user-count">
        {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} found
      </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading students...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredStudents.length === 0 && !error && (
        <div className="empty-state">
          <h3>No students found</h3>
          <p>
            {searchTerm 
              ? 'No students match your search criteria.' 
              : `No students with status "${status}" found.`
            }
          </p>
        </div>
      )}

      {/* Student Table */}
      {!isLoading && filteredStudents.length > 0 && (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Join Date</th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td>
                    <div className="user-name-cell">
                      <div className="user-avatar">
                        {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <span className="user-name">{student.name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{student.email || 'N/A'}</span>
                      </div>
                      <div className="contact-item">
                        <Phone size={14} />
                        <span>{formatPhone(student.phone)}</span>
                      </div>
                    </div>
                  </td>
                  <td>{getStatusBadge(student.status)}</td>
                  <td>{formatDate(student.joinDate)}</td>
                  <td>
                    <div className="progress-cell">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: student.lessonsTotal > 0 
                              ? `${(student.lessonsCompleted / student.lessonsTotal) * 100}%` 
                              : '0%'
                          }}
                        />
                      </div>
                      <span className="progress-text">
                        {student.lessonsCompleted}/{student.lessonsTotal} lessons
                      </span>
                    </div>
                  </td>
                  <td>
                    {status === 'invited' ? (
                      // Invited students: Show only Register button
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleCopyUrl(student)}
                          title="Copy registration URL"
                        >
                          <Copy size={16} />
                          <span className="btn-text">Copy Link</span>
                        </button>
                      </div>
                    ) : (
                      // Other statuses: Show View and Edit buttons
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleView(student.id)}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(student)}
                          title="Edit student"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Student Details Modal (View) */}
      {showDetailsModal && selectedStudentId && (
        <StudentDetailsModal
          studentId={selectedStudentId}
          onClose={handleCloseDetailsModal}
        />
      )}

      {/* Edit Student Modal */}
      {showEditModal && selectedStudentForEdit && (
        <EditStudentModal
          student={selectedStudentForEdit}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default StudentList;